import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUsers } from '../../../store/slices/userSlice';
import { fetchEvent, notifyEventsChanged, updateEvent } from '../services/eventService';
import {
  ACTION_COUNTER_ID,
  actionChartBuckets,
  actionTrackKind,
  entityNameFor,
  formatTrackValue,
  normalizeInput,
  parseEntries,
  parseEntities,
} from '../utils/actionTrackingUtils';

const GRAINS = [
  { id: 'minute', label: 'Minute' },
  { id: 'hour', label: 'Hour' },
  { id: 'day', label: 'Day' },
  { id: 'month', label: 'Month' },
  { id: 'year', label: 'Year' },
];

const stamp = (at) => {
  const t = at instanceof Date ? at : new Date(at);
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(t.getDate())}/${pad(t.getMonth() + 1)}/${t.getFullYear()}  ${pad(t.getHours())}:${pad(t.getMinutes())}`;
};

const ActionTrackingPanel = ({
  eventId,
  eventInfo,
  currentUserId,
  currentUserName = 'You',
  isCreator = false,
  readOnly = false,
  onUpdated,
}) => {
  const dispatch = useDispatch();
  const users = useSelector((state) => state.users?.users || []);
  const [event, setEvent] = useState(eventInfo);
  const [entityId, setEntityId] = useState(null);
  const [grain, setGrain] = useState('day');
  const [value, setValue] = useState('');
  const [choice, setChoice] = useState('');
  const [checks, setChecks] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [fillOpen, setFillOpen] = useState(false);
  const [fillValues, setFillValues] = useState({});
  const [shareOpen, setShareOpen] = useState(false);
  const [entityOpen, setEntityOpen] = useState(false);
  const [entityDraft, setEntityDraft] = useState({ name: '', unit: '', input: 'TEXTBOX', options: [], optionInput: '' });

  useEffect(() => {
    setEvent(eventInfo);
  }, [eventInfo]);

  useEffect(() => {
    dispatch(fetchUsers({ force: false }));
  }, [dispatch]);

  useEffect(() => {
    if (!eventId) return undefined;
    const tick = async () => {
      try {
        const next = await fetchEvent(eventId);
        setEvent(next);
        onUpdated?.(next);
      } catch {
        /* keep last */
      }
    };
    const timer = setInterval(tick, 6000);
    return () => clearInterval(timer);
  }, [eventId, onUpdated]);

  const configuration = event?.configuration || eventInfo?.configuration || {};
  const entities = parseEntities(configuration);
  const entries = parseEntries(configuration);
  const kind = actionTrackKind(configuration, entities);
  const isCount = kind === 'COUNT';
  const selected = entities.find((item) => item.id === entityId) || null;
  const countEntities = entities.filter((item) => item.kind === 'COUNT');
  const people = event?.participants || eventInfo?.participants || [];

  const matching = useMemo(() => {
    if (selected) return entries.filter((item) => item.entityId === selected.id);
    return entries.filter((item) => item.entityId === ACTION_COUNTER_ID || !item.entityId);
  }, [entries, selected]);

  const total = matching.reduce((sum, item) => sum + (Number(item.value) || 0), 0);
  const buckets = actionChartBuckets(matching, selected?.id || ACTION_COUNTER_ID, grain);
  const maxBucket = Math.max(1, ...buckets.map((item) => item.value));
  const unit = selected?.unit || '';
  const canLog = !readOnly;
  const meName = currentUserName || 'You';

  const persistConfig = async (nextConfig, extra = {}) => {
    if (!eventId || saving) return;
    setSaving(true);
    setError('');
    try {
      const updated = await updateEvent(eventId, {
        name: event?.name || eventInfo?.name,
        eventType: 'ACTION_TRACKING',
        configuration: nextConfig,
        ...extra,
      });
      setEvent(updated);
      notifyEventsChanged(updated);
      onUpdated?.(updated);
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const addEntries = async (items) => {
    const now = Date.now();
    const nextEntries = [
      ...(Array.isArray(configuration.entries) ? configuration.entries : []),
      ...items.map((item, index) => ({
        id: `ae-${now}-${index}`,
        entityId: item.entityId,
        value: item.value,
        at: new Date().toISOString(),
        ...(item.label ? { label: item.label } : {}),
        userId: currentUserId || '',
        userName: meName,
      })),
    ];
    await persistConfig({ ...configuration, entries: nextEntries });
    setValue('');
    setChoice('');
    setChecks([]);
  };

  const addSimple = async () => {
    if (isCount) {
      await addEntries([{ entityId: ACTION_COUNTER_ID, value: 1, label: '+1' }]);
      return;
    }
    const parsed = Number(value);
    if (Number.isNaN(parsed)) {
      setError('Enter a number');
      return;
    }
    await addEntries([{ entityId: ACTION_COUNTER_ID, value: parsed, label: String(value).trim() }]);
  };

  const addSelected = async () => {
    if (!selected) return addSimple();
    if (selected.kind === 'COUNT') {
      setFillOpen(true);
      return;
    }
    let nextValue = 0;
    let label = '';
    if (selected.input === 'DROPDOWN' || selected.input === 'RADIO') {
      if (!choice) {
        setError('Pick an option');
        return;
      }
      label = choice;
      nextValue = Number(choice) || 1;
    } else if (selected.input === 'CHECKBOX') {
      if (!checks.length) {
        setError('Pick at least one option');
        return;
      }
      label = checks.join(', ');
      nextValue = checks.length;
    } else {
      const parsed = Number(value);
      if (Number.isNaN(parsed)) {
        setError('Enter a number');
        return;
      }
      nextValue = parsed;
      label = String(value).trim();
    }
    await addEntries([{ entityId: selected.id, value: nextValue, label }]);
  };

  const submitFill = async () => {
    const items = [];
    const targets = countEntities.length ? countEntities : entities;
    targets.forEach((entity) => {
      const field = fillValues[entity.id] || {};
      if (entity.input === 'DROPDOWN' || entity.input === 'RADIO') {
        const picked = String(field.choice || '').trim();
        if (!picked) return;
        items.push({ entityId: entity.id, value: Number(picked) || 1, label: picked });
      } else if (entity.input === 'CHECKBOX') {
        const selectedOpts = field.checks || [];
        if (!selectedOpts.length) return;
        items.push({ entityId: entity.id, value: selectedOpts.length, label: selectedOpts.join(', ') });
      } else {
        const typed = String(field.text || '').trim();
        if (!typed) return;
        items.push({
          entityId: entity.id,
          value: Number(typed) || (entity.kind === 'COUNT' ? 1 : 0),
          label: typed,
        });
      }
    });
    if (!items.length) {
      setError('Fill at least one entity');
      return;
    }
    await addEntries(items);
    setFillOpen(false);
    setFillValues({});
  };

  const addEntity = async () => {
    const name = String(entityDraft.name || '').trim();
    if (!name) return;
    const input = normalizeInput(entityDraft.input);
    const options = (entityDraft.options || []).filter(Boolean);
    if (input !== 'TEXTBOX' && options.length < 2) {
      setError('Add at least two options');
      return;
    }
    const next = [
      ...((configuration.entities || [])),
      {
        id: `en-${Date.now()}`,
        name,
        unit: isCount ? '' : String(entityDraft.unit || '').trim(),
        kind: isCount ? 'COUNT' : 'NUMBER',
        input,
        options,
      },
    ];
    await persistConfig({ ...configuration, entities: next });
    setEntityOpen(false);
    setEntityDraft({ name: '', unit: '', input: 'TEXTBOX', options: [], optionInput: '' });
  };

  const shareWith = async (user) => {
    const existing = people.map((person) => ({
      userId: person.userId,
      userName: person.userName,
      role: person.role || 'REQUIRED',
      status: person.status || 'INVITED',
    }));
    if (existing.some((person) => String(person.userId) === String(user.id))) return;
    await persistConfig(configuration, {
      participants: [
        ...existing,
        {
          userId: user.id,
          userName: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
          role: 'REQUIRED',
          status: 'INVITED',
        },
      ],
    });
    setShareOpen(false);
  };

  const deleteLog = async (id) => {
    const next = (configuration.entries || []).filter((item) => String(item.id) !== String(id));
    await persistConfig({ ...configuration, entries: next });
  };

  const byPerson = {};
  matching.forEach((item) => {
    const who = item.userName || item.userId || 'Someone';
    byPerson[who] = (byPerson[who] || 0) + item.value;
  });

  return (
    <div className="space-y-4">
      {error && <p className="text-xs text-rose-500">{error}</p>}
      <div className="flex flex-wrap gap-2">
        {entities.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => { setEntityId(item.id); setChoice(''); setChecks([]); setValue(''); }}
            className={`px-3 py-1.5 rounded-full text-xs font-bold border ${
              entityId === item.id
                ? 'border-primary-500 bg-primary-50 text-primary-700'
                : 'border-gray-200 dark:border-gray-700'
            }`}
          >
            {item.name}{item.unit ? ` · ${item.unit}` : ''}
          </button>
        ))}
        {isCreator && !readOnly && (
          <button
            type="button"
            onClick={() => setEntityOpen(true)}
            className="px-3 py-1.5 rounded-full text-xs font-bold bg-primary-600 text-white"
          >
            + Add entity
          </button>
        )}
        {isCreator && (
          <button
            type="button"
            onClick={() => setShareOpen((open) => !open)}
            className="px-3 py-1.5 rounded-full text-xs font-bold border border-gray-200 dark:border-gray-700"
          >
            Share
          </button>
        )}
      </div>

      {shareOpen && (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-3 max-h-40 overflow-y-auto">
          {(users || []).filter((user) => user?.id && String(user.id) !== String(currentUserId)).map((user) => (
            <button
              key={user.id}
              type="button"
              onClick={() => shareWith(user)}
              className="block w-full text-left text-sm py-1 hover:text-primary-600"
            >
              {`${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username}
            </button>
          ))}
        </div>
      )}

      <div className="rounded-2xl p-4 text-white bg-gradient-to-r from-teal-400 to-teal-700 text-center">
        <p className="text-sm font-semibold text-white/90">{isCount ? 'Total count' : 'Total'}</p>
        <p className="text-4xl font-black">{formatTrackValue(total)}{unit ? ` ${unit}` : ''}</p>
        <p className="text-sm text-white/80">{selected?.name || (isCount ? 'count' : 'total')}</p>
      </div>

      {(people.length > 1 || Object.keys(byPerson).length > 1) && (
        <div className="flex flex-wrap gap-2">
          {people.map((person) => (
            <span key={person.userId} className="px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-800 text-xs font-semibold">
              {person.userName || 'Member'}
              {byPerson[person.userName] != null ? ` · ${formatTrackValue(byPerson[person.userName])}` : ''}
            </span>
          ))}
        </div>
      )}

      {canLog && (
        <div className="space-y-2">
          {(isCount || countEntities.length > 0) && (
            <button
              type="button"
              disabled={saving}
              onClick={() => (countEntities.length ? setFillOpen(true) : addSimple())}
              className="w-full rounded-xl py-2.5 bg-teal-600 text-white font-bold disabled:opacity-50"
            >
              Count +1
            </button>
          )}
          {(!selected || selected.kind !== 'COUNT') && !isCount && (
            <div className="space-y-2">
              {selected?.input === 'DROPDOWN' && (
                <select value={choice} onChange={(e) => setChoice(e.target.value)} className="w-full rounded-lg border px-3 py-2 bg-white dark:bg-gray-800">
                  <option value="">Select</option>
                  {(selected.options || []).map((option) => <option key={option} value={option}>{option}</option>)}
                </select>
              )}
              {selected?.input === 'RADIO' && (selected.options || []).map((option) => (
                <label key={option} className="flex items-center gap-2 text-sm">
                  <input type="radio" checked={choice === option} onChange={() => setChoice(option)} />
                  {option}
                </label>
              ))}
              {selected?.input === 'CHECKBOX' && (selected.options || []).map((option) => (
                <label key={option} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={checks.includes(option)}
                    onChange={(e) => setChecks((prev) => (e.target.checked ? [...prev, option] : prev.filter((item) => item !== option)))}
                  />
                  {option}
                </label>
              ))}
              {(!selected || selected.input === 'TEXTBOX') && (
                <input
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  className="w-full rounded-lg border px-3 py-2 bg-white dark:bg-gray-800"
                  placeholder={unit ? `Add ${unit}` : 'Add number'}
                />
              )}
              <div className="flex justify-end">
                <button type="button" disabled={saving} onClick={addSelected} className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-bold">
                  Add
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div>
        <p className="text-sm font-bold text-gray-900 dark:text-white">Graph</p>
        <div className="flex flex-wrap gap-2 mt-2">
          {GRAINS.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setGrain(item.id)}
              className={`px-3 py-1 rounded-full text-xs font-bold ${grain === item.id ? 'bg-primary-600 text-white' : 'bg-gray-100 dark:bg-gray-800'}`}
            >
              {item.label} wise
            </button>
          ))}
        </div>
        <div className="mt-3 flex items-end gap-1 h-28 rounded-xl bg-gray-50 dark:bg-gray-800 px-2 py-2">
          {buckets.map((bucket) => (
            <div key={bucket.label + bucket.at} className="flex-1 flex flex-col justify-end items-center h-full">
              <div
                className="w-full rounded-t bg-teal-500 min-h-[2px]"
                style={{ height: `${Math.max(4, (bucket.value / maxBucket) * 100)}%` }}
                title={`${bucket.label}: ${formatTrackValue(bucket.value)}`}
              />
              <span className="text-[9px] text-gray-400 mt-1 truncate w-full text-center">{bucket.label}</span>
            </div>
          ))}
        </div>
      </div>

      <div>
        <p className="text-sm font-bold text-gray-900 dark:text-white">Recent logs</p>
        <p className="text-xs text-gray-500 mt-1">
          {matching.length ? `All-time ${formatTrackValue(total)}${unit ? ` ${unit}` : ''}` : (isCount ? 'No counts yet' : 'No numbers yet')}
        </p>
        <div className="mt-2 space-y-2">
          {matching.slice(0, 30).map((log) => {
            const name = entityNameFor(entities, log.entityId, isCount ? 'Count' : 'Number');
            const valueText = `${formatTrackValue(log.value)}${unit ? ` ${unit}` : ''}`;
            const extra = log.label && log.label.toLowerCase() !== name.toLowerCase() && log.label !== valueText ? log.label : '';
            return (
              <div key={log.id} className="rounded-xl border border-gray-200 dark:border-gray-700 px-3 py-2 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-bold text-gray-900 dark:text-white truncate">
                    {extra ? `${name} · ${extra}` : name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {['Value ' + valueText, log.userName || '', stamp(log.at)].filter(Boolean).join('  ·  ')}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-black text-gray-900 dark:text-white">{valueText}</span>
                  {canLog && (
                    <button type="button" onClick={() => deleteLog(log.id)} className="text-gray-400 hover:text-rose-500">×</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {fillOpen && (
        <div className="fixed inset-0 z-[1400] flex items-end sm:items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white dark:bg-gray-900 p-4 space-y-3 max-h-[80vh] overflow-y-auto">
            <p className="text-lg font-bold">Count +1</p>
            <p className="text-xs text-gray-500">Fill each entity, then add</p>
            {(countEntities.length ? countEntities : entities).map((entity) => {
              const field = fillValues[entity.id] || {};
              return (
                <div key={entity.id} className="rounded-xl border border-teal-200 p-3 space-y-2">
                  <p className="font-bold text-sm">{entity.name}</p>
                  {(entity.input === 'DROPDOWN') && (
                    <select
                      value={field.choice || ''}
                      onChange={(e) => setFillValues((prev) => ({ ...prev, [entity.id]: { ...field, choice: e.target.value } }))}
                      className="w-full rounded-lg border px-3 py-2"
                    >
                      <option value="">Select</option>
                      {(entity.options || []).map((option) => <option key={option} value={option}>{option}</option>)}
                    </select>
                  )}
                  {entity.input === 'RADIO' && (entity.options || []).map((option) => (
                    <label key={option} className="flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        checked={field.choice === option}
                        onChange={() => setFillValues((prev) => ({ ...prev, [entity.id]: { ...field, choice: option } }))}
                      />
                      {option}
                    </label>
                  ))}
                  {entity.input === 'CHECKBOX' && (entity.options || []).map((option) => (
                    <label key={option} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={(field.checks || []).includes(option)}
                        onChange={(e) => {
                          const next = new Set(field.checks || []);
                          if (e.target.checked) next.add(option);
                          else next.delete(option);
                          setFillValues((prev) => ({ ...prev, [entity.id]: { ...field, checks: Array.from(next) } }));
                        }}
                      />
                      {option}
                    </label>
                  ))}
                  {(!entity.input || entity.input === 'TEXTBOX') && (
                    <input
                      value={field.text || ''}
                      onChange={(e) => setFillValues((prev) => ({ ...prev, [entity.id]: { ...field, text: e.target.value } }))}
                      className="w-full rounded-lg border px-3 py-2"
                      placeholder="Type a value"
                    />
                  )}
                </div>
              );
            })}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setFillOpen(false)} className="px-3 py-2 text-sm">Cancel</button>
              <button type="button" onClick={submitFill} className="px-4 py-2 rounded-lg bg-teal-600 text-white font-bold">Add</button>
            </div>
          </div>
        </div>
      )}

      {entityOpen && (
        <div className="fixed inset-0 z-[1400] flex items-end sm:items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white dark:bg-gray-900 p-4 space-y-3">
            <p className="text-lg font-bold">Add entity</p>
            <input
              value={entityDraft.name}
              onChange={(e) => setEntityDraft((prev) => ({ ...prev, name: e.target.value }))}
              className="w-full rounded-lg border px-3 py-2"
              placeholder="Entity name"
            />
            {!isCount && (
              <input
                value={entityDraft.unit}
                onChange={(e) => setEntityDraft((prev) => ({ ...prev, unit: e.target.value }))}
                className="w-full rounded-lg border px-3 py-2"
                placeholder="Unit (optional)"
              />
            )}
            <div className="flex flex-wrap gap-2">
              {['TEXTBOX', 'DROPDOWN', 'RADIO', 'CHECKBOX'].map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setEntityDraft((prev) => ({ ...prev, input: item }))}
                  className={`px-3 py-1 rounded-full text-xs font-bold ${entityDraft.input === item ? 'bg-primary-600 text-white' : 'bg-gray-100'}`}
                >
                  {item}
                </button>
              ))}
            </div>
            {entityDraft.input !== 'TEXTBOX' && (
              <div className="flex gap-2">
                <input
                  value={entityDraft.optionInput}
                  onChange={(e) => setEntityDraft((prev) => ({ ...prev, optionInput: e.target.value }))}
                  className="flex-1 rounded-lg border px-3 py-2"
                  placeholder="Add option"
                />
                <button
                  type="button"
                  onClick={() => {
                    const next = String(entityDraft.optionInput || '').trim();
                    if (!next) return;
                    setEntityDraft((prev) => ({ ...prev, options: [...prev.options, next], optionInput: '' }));
                  }}
                  className="px-3 rounded-lg bg-primary-600 text-white font-bold"
                >
                  +
                </button>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setEntityOpen(false)} className="px-3 py-2 text-sm">Cancel</button>
              <button type="button" onClick={addEntity} className="px-4 py-2 rounded-lg bg-primary-600 text-white font-bold">Add entity</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActionTrackingPanel;
